import { useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { OwnerEntity } from '@bhops/shared';
import { auditLogsApi, type ListAuditLogsQuery } from '@/api/audit-logs';
import { extractApiError } from '@/api/client';
import { kstDateTime } from '@/lib/format-date';
import { Button } from '@/components/Form';
// 개선 9건 차수2(26-08-04) §3 — 표기(액션 이름·문장·diff 필드명·diff 값) 정본 한 벌.
// 값 포맷터(formatDiffValue)는 종전에 이 파일 안의 지역 함수였는데, 문장 렌더러가
// 같은 판정을 다시 구현하면 표와 문장이 서로 다른 말을 하게 되므로 lib 으로 옮겼다.
import {
  asFieldDiff,
  formatAuditActorLabel,
  formatAuditLogAction,
  formatAuditLogSentence,
  formatDiffFieldLabel,
  formatDiffValue,
  asDeleteSnapshot,
  formatDeleteSnapshotFieldLabel,
} from '@/lib/audit-log-format';

const ENTITY_LABEL: Record<OwnerEntity, string> = {
  HONEYCOMB_ROBOTICS: '허니컴로보틱스',
  BEYOND_HONEYCOMB: '비욘드허니컴',
};

// Common action prefixes shown as quick filters. Typed literals stay
// in one place so the UI doesn't drift from the action strings emitted
// by the AuditInterceptor and AuditOutboxConsumer.
const ACTION_PRESETS: Array<{ label: string; value: string }> = [
  { label: '전체', value: '' },
  // round5 #18 — 고객사 마스터 수정 이력만 골라 보기. 서버 action 필터가
  // prefix 와일드카드(customer-master.*)를 지원하는데 프리셋이 없어
  // 운영자가 문법을 알 수 없었다.
  { label: '고객사 마스터', value: 'customer-master.*' },
  { label: '인증', value: 'auth.*' },
  { label: '로봇', value: 'robot.*' },
  // 개선 9건 차수2(26-08-04) §3 — 종전 값은 'robotqr.*' 였는데 서버가 그 접두사로
  // 기록한 적이 없어(git log -S 0건) **항상 0건**이 나오던 프리셋이다. 현재 액션은
  // robot.qr.issue / robot.qr.revoke 다.
  { label: 'QR', value: 'robot.qr.*' },
  { label: '배치', value: 'deployment.*' },
  { label: '리포트', value: 'customerreport.*' },
  { label: 'DSAR', value: 'privacy-request.*' },
  { label: '사용자', value: 'user.*' },
];

const PAGE_SIZE = 50;

export function AuditLogsPage() {
  // round5 #18(역제안) — ?action=…&resourceId=… 딥링크 지원. 고객사 마스터
  // 행의 '변경 이력' 점프가 이 쿼리로 진입한다. 마운트 시 1회만 읽어 초기
  // 필터에 반영(이후 사용자가 자유롭게 변경).
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<ListAuditLogsQuery>(() => ({
    page: 1,
    pageSize: PAGE_SIZE,
    action: searchParams.get('action') ?? undefined,
    resourceId: searchParams.get('resourceId') ?? undefined,
    // 오너 지적(2026-08-31) — 로봇 상세의 '이 로봇 기록 보기' 가 이 쿼리로 진입한다.
    robotSerial: searchParams.get('robotSerial') ?? undefined,
  }));
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // 감사 보강 ①-D(2026-08-28) — 장부 위·변조 검증. 누를 때만 돈다(전 행을 훑으므로
  // 화면을 열 때마다 돌리지 않는다). 결과는 화면에만 남긴다 — 감사 장부에 다시
  // 쓰면 되먹임 고리가 생긴다.
  const verifyM = useMutation({ mutationFn: () => auditLogsApi.verifyChain() });

  const listQ = useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: () => auditLogsApi.list(filters),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });

  const selectedQ = useQuery({
    queryKey: ['audit-log', selectedId],
    queryFn: () => auditLogsApi.get(selectedId!),
    enabled: Boolean(selectedId),
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const page = listQ.data?.page ?? 1;
  const pageSize = listQ.data?.pageSize ?? PAGE_SIZE;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));

  function updateFilter<K extends keyof ListAuditLogsQuery>(
    key: K,
    value: ListAuditLogsQuery[K],
  ): void {
    setFilters((f) => ({ ...f, [key]: value || undefined, page: 1 }));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          감사 로그
        </h1>
        {/* 감사 보강 ①-D(2026-08-28) — 장부 자체의 위·변조 검증.
            종전엔 검증 장치가 만들어져 있으면서 **아무 데서도 불리지 않았다.**
            매일 05:00 KST 크론이 돌지만, 관리자가 지금 확인하고 싶을 때
            누를 자리도 있어야 한다. */}
        <button
          type="button"
          className="kuun-btn"
          data-testid="audit-verify-chain-button"
          disabled={verifyM.isPending}
          onClick={() => verifyM.mutate()}
        >
          {verifyM.isPending ? '검증 중…' : '기록 위·변조 검사'}
        </button>
        <div
          className="kuun-caption"
          style={{
            marginLeft: 'auto',
            color: listQ.isFetching ? 'var(--brand)' : 'var(--fg-muted)',
          }}
        >
          {listQ.isFetching ? '불러오는 중…' : `${total.toLocaleString('ko-KR')} 건`}
        </div>
      </div>

      {verifyM.data && (
        <div
          data-testid="audit-verify-chain-result"
          style={{
            padding: 10,
            borderRadius: 6,
            border: '1px solid var(--border-default)',
            background: verifyM.data.issues.length > 0 ? 'var(--danger-bg)' : 'var(--bg-subtle)',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {verifyM.data.issues.length > 0
              ? `고쳐진 흔적 ${verifyM.data.issues.length}건이 발견됐습니다`
              : '고쳐진 흔적이 없습니다'}
          </div>
          <div className="kuun-caption">
            {`검사한 기록 ${verifyM.data.verified.toLocaleString('ko-KR')}건`}
            {verifyM.data.skipped > 0 &&
              `, 검사 대상이 아닌 옛 기록 ${verifyM.data.skipped.toLocaleString('ko-KR')}건`}
            {verifyM.data.legacyDiffRows > 0 &&
              `, 확인할 수 없는 옛 기록 ${verifyM.data.legacyDiffRows.toLocaleString('ko-KR')}건`}
          </div>
          {/* '확인 불가'를 '이상 없음'에 섞지 않는다 — 섞으면 장부가 스스로를
              과대평가한다. 왜 확인할 수 없는지 한 줄로 밝힌다. */}
          {verifyM.data.legacyDiffRows > 0 && (
            <div className="kuun-caption" style={{ marginTop: 4, color: 'var(--fg-muted)' }}>
              확인할 수 없는 기록은 날짜 칸이 검사 대상에 들어가지 않던 2026-08-28 이전의 것입니다.
              그 칸이 고쳐졌더라도 이 검사로는 드러나지 않습니다.
            </div>
          )}
          {verifyM.data.issues.slice(0, 10).map((issue) => (
            <div key={issue.rowId} className="kuun-caption" style={{ marginTop: 4 }}>
              {kstDateTime(issue.occurredAt)} ·{' '}
              {issue.kind === 'prevHash-mismatch'
                ? '기록의 순서나 개수가 바뀐 흔적'
                : '기록의 내용이 고쳐진 흔적'}{' '}
              · {issue.rowId}
            </div>
          ))}
        </div>
      )}
      {verifyM.isError && (
        <div className="kuun-caption" data-testid="audit-verify-chain-error">
          검사에 실패했습니다. 잠시 후 다시 시도해 주세요.
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          padding: 8,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-surface)',
        }}
      >
        {ACTION_PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => updateFilter('action', p.value)}
            style={{
              padding: '4px 10px',
              fontSize: 12,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-pill)',
              background:
                (filters.action ?? '') === p.value ? 'var(--brand-subtle)' : 'var(--bg-surface)',
              color: (filters.action ?? '') === p.value ? 'var(--brand)' : 'var(--fg-default)',
              cursor: 'pointer',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
        <label style={filterLabel}>
          <span className="kuun-label">액션</span>
          <input
            style={input}
            placeholder="예: robot.create, auth.*"
            value={filters.action ?? ''}
            onChange={(e) => updateFilter('action', e.target.value)}
          />
        </label>
        <label style={filterLabel}>
          <span className="kuun-label">리소스 타입</span>
          <input
            style={input}
            placeholder="예: Robot, CustomerReport"
            value={filters.resourceType ?? ''}
            onChange={(e) => updateFilter('resourceType', e.target.value)}
          />
        </label>
        <label style={filterLabel}>
          <span className="kuun-label">리소스 ID</span>
          <input
            style={input}
            placeholder="cuid / displayId"
            value={filters.resourceId ?? ''}
            onChange={(e) => updateFilter('resourceId', e.target.value)}
          />
        </label>
        {/* 오너 지적(2026-08-31) "#113번의 기록을 찾을 수가 없음" — '리소스 ID' 는
            내부 id 정확 일치라 로봇 번호로는 늘 0건이었다. 번호로 찾는 칸을 따로 둔다
            (한 칸이 두 뜻을 겸하지 않는다). 번호에 해당하는 로봇이 없으면 0건이 답이다. */}
        <label style={filterLabel}>
          <span className="kuun-label">로봇 번호</span>
          <input
            style={input}
            data-testid="audit-filter-robot-serial"
            placeholder="예: 113"
            value={filters.robotSerial ?? ''}
            onChange={(e) => updateFilter('robotSerial', e.target.value)}
          />
        </label>
        <label style={filterLabel}>
          <span className="kuun-label">법인</span>
          <select
            style={select}
            value={filters.ownerEntity ?? ''}
            onChange={(e) =>
              updateFilter('ownerEntity', (e.target.value || undefined) as OwnerEntity | undefined)
            }
          >
            <option value="">전체</option>
            {Object.entries(ENTITY_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label style={filterLabel}>
          <span className="kuun-label">결과</span>
          <select
            style={select}
            value={filters.outcome ?? ''}
            onChange={(e) =>
              updateFilter(
                'outcome',
                (e.target.value || undefined) as 'success' | 'failure' | undefined,
              )
            }
          >
            <option value="">전체</option>
            <option value="success">성공</option>
            <option value="failure">실패</option>
          </select>
        </label>
        <label style={filterLabel}>
          <span className="kuun-label">시작일</span>
          <input
            style={input}
            type="date"
            value={filters.fromDate?.slice(0, 10) ?? ''}
            onChange={(e) =>
              updateFilter(
                'fromDate',
                e.target.value ? new Date(e.target.value).toISOString() : undefined,
              )
            }
          />
        </label>
        <label style={filterLabel}>
          <span className="kuun-label">종료일</span>
          <input
            style={input}
            type="date"
            value={filters.toDate?.slice(0, 10) ?? ''}
            onChange={(e) =>
              updateFilter(
                'toDate',
                e.target.value
                  ? new Date(
                      new Date(e.target.value).getTime() + 24 * 60 * 60_000 - 1,
                    ).toISOString()
                  : undefined,
              )
            }
          />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-card)',
            background: 'var(--bg-surface)',
            overflow: 'auto',
          }}
        >
          {listQ.isLoading ? (
            <div style={empty}>
              <span className="kuun-caption">불러오는 중…</span>
            </div>
          ) : listQ.error ? (
            <div style={empty}>
              <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(listQ.error)}</span>
            </div>
          ) : items.length === 0 ? (
            <div style={empty}>
              <div className="kuun-h4">조건에 맞는 감사 로그가 없습니다</div>
              {/* 오너 지적(2026-08-31) — 번호로 찾았는데 0건일 때 두 가지 뜻이 있다:
                  그런 번호의 로봇이 없거나, 그 로봇에 남은 기록이 없거나. 어느
                  쪽인지 화면이 단정하지 않고 **둘 다 있음을 알린다** — 하나로
                  뭉개면 사람이 없는 원인을 쫓는다. */}
              {filters.robotSerial ? (
                <div
                  className="kuun-caption"
                  data-testid="audit-empty-robot-serial"
                  style={{ marginTop: 4 }}
                >
                  {`'${filters.robotSerial}' 번호로 남은 기록이 없습니다. 번호가 맞는지 확인해 주시고, 번호가 맞다면 그 로봇에 아직 변경 기록이 없다는 뜻입니다.`}
                </div>
              ) : (
                <div className="kuun-caption" style={{ marginTop: 4 }}>
                  필터를 바꾸거나 "전체"를 눌러 다시 찾아 보세요.
                </div>
              )}
            </div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={th}>시각</th>
                  <th style={th}>액션</th>
                  <th style={th}>리소스</th>
                  <th style={th}>행위자</th>
                  <th style={th}>법인</th>
                  <th style={th}>결과</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelectedId(r.id)}
                    style={{
                      cursor: 'pointer',
                      borderTop: '1px solid var(--border-subtle)',
                      background: selectedId === r.id ? 'var(--bg-selected)' : undefined,
                    }}
                  >
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {/* 추가수정 2차(26-07-27) §2 — 브라우저 로캘 대신 KST 고정.
                          감사 로그는 시각 자체가 기록이라 날짜만으로 줄이지 않는다. */}
                      {kstDateTime(r.occurredAt) ?? '–'}
                    </td>
                    <td style={td}>
                      {/* 개선 9건 차수2(26-08-04) §3 — 목록이 "누가 무엇을 어떻게"를
                          한 문장으로 말한다. 원문 액션은 아래 캡션에 그대로 남겨
                          정확한 액션으로 필터하는 길을 막지 않는다. */}
                      <div data-testid="audit-sentence">
                        {formatAuditLogSentence({
                          action: r.action,
                          actorUserId: r.actorUserId,
                          actorDisplayName: r.actor?.displayName,
                          // 오너 지적(2026-08-31) — 문장이 "어느 것을" 말한다.
                          resourceLabel: r.resourceLabel,
                          diff: r.diff,
                        })}
                      </div>
                      <div
                        className="kuun-caption"
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          color: 'var(--fg-muted)',
                        }}
                      >
                        {r.action}
                      </div>
                    </td>
                    <td style={td}>
                      {/* 오너 지적(2026-08-31) "어떤 로봇에 대한 변경 기록인지가 불분명함" —
                          종전엔 이 칸이 `Robot` + cuid 두 줄뿐이었다. 이름이 풀리면
                          **이름을 맨 위**에 굵게 두고 종류·원시 id 는 근거로 내린다.
                          이름이 안 풀리는 행(대상 없음·이름 표에 없는 종류·완전삭제)은
                          종전 모양 그대로다 — 없는 이름을 지어내지 않는다. */}
                      {r.resourceLabel && (
                        <div data-testid="audit-resource-label" style={{ fontWeight: 600 }}>
                          {r.resourceLabel}
                        </div>
                      )}
                      <div className={r.resourceLabel ? 'kuun-caption' : undefined}>
                        {r.resourceType}
                      </div>
                      {r.resourceId && (
                        <div
                          className="kuun-caption"
                          style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}
                        >
                          {r.resourceId}
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      {/* 개선 9건 차수2(26-08-04) §3 — 작성자 표기 정본:
                          이름 / '(삭제된 계정)' / '—'(작성자 자체가 없는 행).
                          종전의 'system / anonymous' 는 영문이고, 계정이 지워진
                          기록과 시스템 작업을 같은 말로 뭉갰다. */}
                      {r.actor?.displayName ?? (
                        <span
                          className="kuun-caption"
                          title="시스템 작업이거나 로그인 없이 들어온 공개 요청이라 작성자가 기록되지 않습니다"
                        >
                          {formatAuditActorLabel(r.actorUserId, r.actor?.displayName)}
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      {r.ownerEntity ? (
                        (ENTITY_LABEL[r.ownerEntity] ?? r.ownerEntity)
                      ) : (
                        <span className="kuun-caption">—</span>
                      )}
                    </td>
                    <td style={td}>
                      {r.outcome === 'failure' ? (
                        <span style={failureBadge}>실패</span>
                      ) : (
                        <span className="kuun-caption">성공</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            width: 400,
            flexShrink: 0,
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-card)',
            background: 'var(--bg-surface)',
            overflow: 'auto',
          }}
        >
          {!selectedId ? (
            <div style={empty}>
              <span className="kuun-caption">행을 선택하면 상세가 표시됩니다.</span>
            </div>
          ) : selectedQ.isLoading ? (
            <div style={empty}>
              <span className="kuun-caption">불러오는 중…</span>
            </div>
          ) : selectedQ.error ? (
            <div style={empty}>
              <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(selectedQ.error)}</span>
            </div>
          ) : (
            selectedQ.data && (
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* 개선 9건 차수2(26-08-04) §3 — 상세의 첫 줄은 한 문장이다.
                    아래 칸들은 그 문장의 근거(원문 액션·리소스·diff 표). */}
                <div
                  data-testid="audit-sentence-detail"
                  style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}
                >
                  {formatAuditLogSentence({
                    action: selectedQ.data.action,
                    actorUserId: selectedQ.data.actorUserId,
                    actorDisplayName: selectedQ.data.actor?.displayName,
                    resourceLabel: selectedQ.data.resourceLabel,
                    diff: selectedQ.data.diff,
                  })}
                </div>
                <KV k="ID" v={selectedQ.data.id} mono />
                <KV k="시각" v={kstDateTime(selectedQ.data.occurredAt, { label: true }) ?? '–'} />
                <KV
                  k="액션"
                  v={
                    <>
                      <span>{formatAuditLogAction(selectedQ.data.action)}</span>
                      <span
                        className="kuun-caption"
                        style={{
                          marginLeft: 6,
                          fontFamily: 'var(--font-mono)',
                          color: 'var(--fg-muted)',
                        }}
                      >
                        {selectedQ.data.action}
                      </span>
                    </>
                  }
                />
                {/* 오너 지적(2026-08-31) — 대상을 사람이 아는 이름으로 먼저 말하고,
                    로봇이면 그 로봇 화면으로 바로 갈 길을 준다. 이름이 안 풀린 행은
                    이 칸이 아예 없다(빈 칸을 그려 "이름이 없다"를 "이름이 비었다"로
                    보이게 하지 않는다). */}
                {selectedQ.data.resourceLabel && (
                  <KV
                    k="대상"
                    v={
                      selectedQ.data.resourceType === 'Robot' && selectedQ.data.resourceId ? (
                        <Link
                          to={`/robots/${selectedQ.data.resourceId}`}
                          style={{ color: 'var(--fg-brand)', fontWeight: 600 }}
                        >
                          {selectedQ.data.resourceLabel}
                        </Link>
                      ) : (
                        <span style={{ fontWeight: 600 }}>{selectedQ.data.resourceLabel}</span>
                      )
                    }
                  />
                )}
                <KV k="리소스" v={selectedQ.data.resourceType} />
                {selectedQ.data.resourceId && (
                  <KV k="리소스 ID" v={selectedQ.data.resourceId} mono />
                )}
                <KV
                  k="행위자"
                  v={formatAuditActorLabel(
                    selectedQ.data.actorUserId,
                    selectedQ.data.actor?.displayName,
                  )}
                />
                {/* 이름이 안 풀린 행은 원시 id 를 따로 보여 준다 — 표기를 한국어로
                    바꾸면서 추적 단서를 없애지 않는다(조용한 유실 금지). */}
                {!selectedQ.data.actor?.displayName && selectedQ.data.actorUserId && (
                  <KV k="행위자 ID" v={selectedQ.data.actorUserId} mono />
                )}
                {selectedQ.data.ownerEntity && (
                  <KV
                    k="법인"
                    v={ENTITY_LABEL[selectedQ.data.ownerEntity] ?? selectedQ.data.ownerEntity}
                  />
                )}
                <KV k="결과" v={selectedQ.data.outcome} />
                {selectedQ.data.ip && <KV k="IP" v={selectedQ.data.ip} mono />}
                {selectedQ.data.requestId && <KV k="요청 ID" v={selectedQ.data.requestId} mono />}
                {selectedQ.data.errorMessage && (
                  <div>
                    <div className="kuun-label" style={{ marginBottom: 4 }}>
                      오류 내용
                    </div>
                    <div style={{ ...codeBlock, color: 'var(--fg-danger)' }}>
                      {selectedQ.data.errorMessage}
                    </div>
                  </div>
                )}
                {selectedQ.data.diff !== null && selectedQ.data.diff !== undefined && (
                  <div>
                    <div className="kuun-label" style={{ marginBottom: 4 }}>
                      변경 내용
                    </div>
                    {/* round5 #18 — 필드단위 diff({field:{from,to}})는 운영자가
                        읽을 수 있는 '변경 전 → 후' 표로. 그 외 형태(과거 행·타
                        리소스의 자유 payload)는 기존 raw JSON 폴백 유지.
                        차수2(26-08-04) §3 — 필드명은 액션까지 보고 고른다(같은
                        'status' 가 고객사 진행 상태와 로봇 상태 둘 다이므로). 빈
                        diff({})는 "저장은 됐고 바뀐 값이 없다"는 서버의 기록이라
                        그 말을 그대로 보여 준다(종전엔 '{}' 를 찍었다). */}
                    {(() => {
                      // 감사 보강 ①-B(2026-08-28) — 완전삭제 스냅샷은 필드 diff 가
                      // 아니라 { deleted, sideEffects } 봉투다. 종전엔 이 형태가
                      // **날 JSON** 으로 그려져 관리자가 영문 키를 읽어야 했다.
                      // 오너 요구("한글로, 누가 언제 무엇을 어떻게")에 맞춰 전용 표로.
                      const snapshot = asDeleteSnapshot(selectedQ.data.diff);
                      if (snapshot) {
                        const section = (
                          title: string,
                          rows: Array<{ field: string; value: unknown }>,
                          testid: string,
                        ) =>
                          rows.length === 0 ? null : (
                            <div style={{ marginBottom: 8 }} data-testid={testid}>
                              <div className="kuun-caption" style={{ marginBottom: 4 }}>
                                {title}
                              </div>
                              <table style={diffTable}>
                                <thead>
                                  <tr>
                                    <th style={diffTh}>항목</th>
                                    <th style={diffTh}>값</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map(({ field, value }) => (
                                    <tr
                                      key={field}
                                      style={{ borderTop: '1px solid var(--border-subtle)' }}
                                    >
                                      <td style={diffTd}>
                                        {formatDeleteSnapshotFieldLabel(
                                          field,
                                          selectedQ.data.action,
                                        )}
                                      </td>
                                      <td style={diffTd}>{formatDiffValue(value, field)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        return (
                          <>
                            {section('삭제 직전 내용', snapshot.deleted, 'audit-snapshot-deleted')}
                            {section(
                              '이 삭제로 함께 벌어진 일',
                              snapshot.sideEffects,
                              'audit-snapshot-side-effects',
                            )}
                          </>
                        );
                      }
                      const fieldDiff = asFieldDiff(selectedQ.data.diff);
                      if (!fieldDiff) {
                        return (
                          <pre style={codeBlock}>
                            {JSON.stringify(selectedQ.data.diff, null, 2)}
                          </pre>
                        );
                      }
                      const rows = Object.entries(fieldDiff);
                      if (rows.length === 0) {
                        return (
                          <div className="kuun-caption" data-testid="audit-diff-empty">
                            저장은 기록됐지만 바뀐 값은 없습니다.
                          </div>
                        );
                      }
                      return (
                        <table style={diffTable}>
                          <thead>
                            <tr>
                              <th style={diffTh}>필드</th>
                              <th style={diffTh}>변경 전</th>
                              <th style={diffTh}>변경 후</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map(([field, change]) => (
                              <tr
                                key={field}
                                style={{ borderTop: '1px solid var(--border-subtle)' }}
                              >
                                <td style={diffTd}>
                                  {formatDiffFieldLabel(field, selectedQ.data.action)}
                                </td>
                                <td style={{ ...diffTd, color: 'var(--fg-muted)' }}>
                                  {formatDiffValue(change.from, field)}
                                </td>
                                <td style={diffTd}>{formatDiffValue(change.to, field)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                )}
                {selectedQ.data.userAgent && (
                  <div>
                    <div className="kuun-label" style={{ marginBottom: 4 }}>
                      User-Agent
                    </div>
                    <div style={{ ...codeBlock, fontSize: 11 }}>{selectedQ.data.userAgent}</div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>

      {maxPage > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
          }}
        >
          <Button
            type="button"
            variant="secondary"
            onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, (f.page ?? 1) - 1) }))}
            disabled={page <= 1}
          >
            이전
          </Button>
          <span className="kuun-caption">
            {page} / {maxPage}
          </span>
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              setFilters((f) => ({ ...f, page: Math.min(maxPage, (f.page ?? 1) + 1) }))
            }
            disabled={page >= maxPage}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
      <div style={{ width: 96, color: 'var(--fg-muted)' }}>{k}</div>
      <div style={{ flex: 1, minWidth: 0, fontFamily: mono ? 'var(--font-mono)' : undefined }}>
        {v}
      </div>
    </div>
  );
}

const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'top' };
const empty: CSSProperties = {
  height: '100%',
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--fg-muted)',
  padding: 32,
};
const filterLabel: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 120,
};
const input: CSSProperties = {
  height: 30,
  padding: '0 8px',
  fontSize: 12,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontFamily: 'inherit',
};
const select: CSSProperties = { ...input, padding: '0 6px' };
const failureBadge: CSSProperties = {
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 'var(--r-pill)',
  fontSize: 10,
  fontWeight: 700,
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  border: '1px solid var(--fg-danger)',
};
const codeBlock: CSSProperties = {
  padding: 10,
  background: 'var(--bg-sunken)',
  borderRadius: 'var(--r-card)',
  fontSize: 12,
  fontFamily: 'var(--font-mono)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
};

// ── round5 #18 의 diff 표 렌더 헬퍼(isFieldDiff / formatDiffValue)는 차수2
//    (26-08-04) §3 에서 lib/audit-log-format 으로 옮겼다 — 문장 렌더러가 같은
//    판정을 필요로 하는데, 여기 두면 표와 문장이 다른 말을 하게 된다.

const diffTable: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 4,
};
const diffTh: CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  color: 'var(--fg-muted)',
  fontWeight: 600,
  borderBottom: '1px solid var(--border-subtle)',
};
const diffTd: CSSProperties = {
  padding: '6px 8px',
  verticalAlign: 'top',
  wordBreak: 'break-all',
};
